/**
 * useToolExecutorRegistry
 *
 * Hook responsável por registrar todos os executores de ferramentas
 * para atalhos de teclado.
 *
 * Extração: Fase 3 - Refatoração ToolsMenu.tsx
 * @author Simplificação 2026-01-13
 */

import { useEffect } from 'react';
import { sendToTerminal } from '@/lib/terminalLogger';
import type { useToolsState } from './useToolsState';
import type { useToolExecution } from './useToolExecution';

type ToolsStateHook = ReturnType<typeof useToolsState>;
type ToolExecutionHook = ReturnType<typeof useToolExecution>;

interface ExecutorRegistration {
  toolId: string;
  actionId: string;
  exec: () => void;
}

/**
 * Hook para registrar executores de ferramentas no KeyboardContext
 *
 * Registra 24 ferramentas com seus actionIds correspondentes.
 * IMPORTANTE: Os actionIds aqui DEVEM corresponder aos do tools.registry.ts
 *
 * @param registerExecutor Função para registrar executor
 * @param unregisterExecutor Função para desregistrar executor
 * @param toolsState Hook de estado das ferramentas
 * @param toolExecution Hook de execução das ferramentas
 */
export function useToolExecutorRegistry(
  registerExecutor: (toolId: string, actionId: string, exec: () => void) => void,
  unregisterExecutor: (toolId: string, actionId: string) => void,
  toolsState: ToolsStateHook,
  toolExecution: ToolExecutionHook
) {
  useEffect(() => {
    // Lista completa de executores
    const regs: ExecutorRegistration[] = [
      // ✅ Ferramentas que abrem modal (usam 'abrir-modal' como actionId)
      // Corresponde a ferramentas com 'opensModalId' no tools.registry.ts
      { toolId: 'oneregua', actionId: 'abrir-modal', exec: () => toolsState.setShowOneRegua(true) },
      { toolId: 'onebitmapv1', actionId: 'abrir-modal', exec: () => toolsState.setShowOneBitmapV1(true) },
      { toolId: 'montatech', actionId: 'abrir-modal', exec: () => toolsState.setShowMontaTech(true) },
      { toolId: 'moldeone-v2', actionId: 'abrir-modal', exec: () => toolsState.setShowMoldeOne(true) },
      { toolId: 'testsmb', actionId: 'abrir-modal', exec: () => toolsState.setShowTestSmb(true) },

      // ✅ Ferramentas com actionId específico (deve corresponder ao tools.registry.ts)
      { toolId: 'oneagrupar', actionId: 'oneagrupar', exec: () => { toolExecution.executeOneAgruparDirect(); } },
      { toolId: 'cmd_montatec', actionId: 'command:1', exec: () => { toolExecution.executeCommand(1); } },
      { toolId: 'cmd_mult_montatec', actionId: 'command:2', exec: () => { toolExecution.executeCommand(2); } },
      { toolId: 'moldesone', actionId: 'command:3', exec: () => { toolExecution.executeCommand(3); } },
      { toolId: 'onecontour', actionId: 'command:4', exec: () => { toolExecution.executeCommand(4); } },
      { toolId: 'onecontour-v2', actionId: 'abrir-modal', exec: () => toolsState.setShowOneContorno(true) },
      { toolId: 'resize', actionId: 'command:5', exec: () => { toolExecution.executeCommand(5); } },
      { toolId: 'transparencia', actionId: 'command:6', exec: () => { toolExecution.executeCommand(6); } },
      { toolId: 'smartimport', actionId: 'smartimport', exec: () => { toolExecution.executeSmartImport(); } },
      { toolId: 'open-free-file', actionId: 'open-free-file', exec: () => { toolExecution.executeOpenFreeFile(); } },
      { toolId: 'runps', actionId: 'runps', exec: () => { toolsState.setShowRunPsConfirm(true); } },
      { toolId: 'quotation-calculator', actionId: 'quotation-calculator', exec: () => { toolsState.openQuotationCalculator(); } },
      { toolId: 'export_batch_local', actionId: 'export_batch_local', exec: () => { toolExecution.executeExportBatchLocal(); } },
      { toolId: 'export_producao_offline', actionId: 'export_producao_offline', exec: () => { toolsState.setShowExportProducaoOffline(true); } },
      { toolId: 'multiplicador-objetos', actionId: 'multiplicador-objetos', exec: () => { toolExecution.executeMultiplicadorObjetos(); } },
      { toolId: 'grid-objetos', actionId: 'grid-objetos', exec: () => { toolExecution.executeGridObjetos(); } },
      { toolId: 'contracao-dtf-textil', actionId: 'contracao-dtf-textil', exec: () => { toolExecution.executeContracaoDtfTextil(); } },
      { toolId: 'contracao-dtf-uv', actionId: 'contracao-dtf-uv', exec: () => { toolExecution.executeContracaoDtfUv(); } },
      { toolId: 'girar-objeto', actionId: 'girar-objeto', exec: () => { toolExecution.executeGirarObjeto(); } },
      { toolId: 'reverter-pipeline', actionId: 'reverter-pipeline', exec: () => { toolExecution.executeReverterPipeline(); } },
      { toolId: 'layout-grid', actionId: 'layout-grid', exec: () => { toolExecution.executeLayoutGrid(); } },
      { toolId: 'gerenciar-camadas', actionId: 'gerenciar-camadas', exec: () => { toolExecution.executeGerenciarCamadas(); } },
      { toolId: 'close-document', actionId: 'close-document', exec: () => { toolExecution.executeCloseDocument(); } },
      { toolId: 'auto-bitmap', actionId: 'auto-bitmap', exec: () => { toolExecution.executeAutoBitmap(); } },
      { toolId: 'montatech-native', actionId: 'montatech-native', exec: () => { toolExecution.executeMontaTechNative(); } },
    ];

    // Registrar todos os executores
    regs.forEach(r => {
      try {
        registerExecutor(r.toolId, r.actionId, r.exec);
      } catch (err) {
        sendToTerminal('⚠️ [SHORTCUTS] Erro ao registrar executor', {
          toolId: r.toolId,
          actionId: r.actionId,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    });

    // Cleanup: desregistrar todos ao desmontar
    return () => {
      regs.forEach(r => {
        try { unregisterExecutor(r.toolId, r.actionId); } catch { }
      });
    };
  }, [registerExecutor, unregisterExecutor, toolsState, toolExecution]);
}
